import { useEffect, useState } from "react";
import axios from "axios";
import { KeyRound, LogOut, RefreshCw } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (d) =>
  new Date(d).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function AdminPage() {
  const [key, setKey] = useState(() => localStorage.getItem("onall_admin_key") || "");
  const [quotes, setQuotes] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    if (!k) return;
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/quotes`, { headers: { "x-admin-key": k } });
      setQuotes(r.data);
      localStorage.setItem("onall_admin_key", k);
    } catch {
      setError("Anahtar hatalı veya sunucuya ulaşılamadı.");
      setQuotes(null);
      localStorage.removeItem("onall_admin_key");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem("onall_admin_key");
    if (saved) load(saved);
  }, []);

  const logout = () => {
    localStorage.removeItem("onall_admin_key");
    setKey("");
    setQuotes(null);
  };

  if (!quotes) {
    return (
      <div className="grain flex min-h-screen items-center justify-center bg-paper px-5">
        <div className="w-full max-w-md rounded-2xl border border-line bg-white p-10 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)]">
          <Logo />
          <p className="font-mono mt-8 text-[11px] font-bold tracking-[0.25em] text-obsidian/50">TALEP PANELİ</p>
          <h1 className="font-display font-expanded mt-2 text-2xl font-black uppercase text-obsidian">Teklif Talepleri</h1>
          <label className="mt-8 block">
            <span className="font-mono mb-2 block text-[10px] font-bold tracking-[0.2em] text-obsidian/60">
              ADMIN ANAHTARI
            </span>
            <div className="flex items-center gap-2 rounded-lg border border-line bg-paper px-4">
              <KeyRound className="h-4 w-4 shrink-0 text-obsidian/40" />
              <input
                data-testid="admin-key-input"
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load(key)}
                placeholder="••••••••"
                className="w-full bg-transparent py-3 text-sm text-obsidian outline-none"
              />
            </div>
          </label>
          {error && (
            <p data-testid="admin-error" className="mt-3 text-xs font-semibold text-[#E8394A]">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            onClick={() => load(key)}
            disabled={loading}
            className="mt-6 w-full rounded-full bg-brand py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-obsidian disabled:opacity-60"
          >
            {loading ? "Kontrol ediliyor…" : "Giriş Yap"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      <header className="sticky top-0 z-40 bg-obsidian">
        <div className="mx-auto flex h-16 max-w-[1320px] items-center justify-between px-5 sm:px-8">
          <Logo light />
          <div className="flex items-center gap-2.5">
            <button
              data-testid="admin-refresh-button"
              onClick={() => load(key)}
              className="flex items-center gap-2 rounded-full border border-paper/20 px-4 py-2 text-xs font-bold text-paper transition-colors duration-300 hover:border-brand hover:text-brand"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Yenile
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-paper/10 px-4 py-2 text-xs font-bold text-paper transition-colors duration-300 hover:bg-brand hover:text-white"
            >
              <LogOut className="h-3.5 w-3.5" />
              Çıkış
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1320px] px-5 py-10 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display font-expanded text-2xl font-black uppercase tracking-tight text-obsidian sm:text-3xl">
            Teklif Talepleri
          </h1>
          <p data-testid="admin-count" className="font-mono text-xs font-bold tracking-[0.2em] text-obsidian/50">
            {quotes.length} TALEP
          </p>
        </div>

        <div data-testid="admin-quotes-table" className="mt-8 overflow-x-auto rounded-2xl border border-line bg-white">
          <table className="w-full min-w-[1040px] text-left text-sm">
            <thead>
              <tr className="font-mono border-b border-line text-[10px] uppercase tracking-[0.15em] text-obsidian/50">
                {["Tarih", "Ad / Firma", "İletişim", "Hizmet", "Kategori", "Hacim", "Mesaj"].map((h) => (
                  <th key={h} className="px-5 py-4 font-bold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {quotes.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-14 text-center text-sm text-obsidian/50">
                    Henüz teklif talebi yok.
                  </td>
                </tr>
              )}
              {quotes.map((q, i) => (
                <tr
                  key={q.id || i}
                  data-testid={`admin-quote-row-${i + 1}`}
                  className="border-b border-line/70 transition-colors duration-200 last:border-0 hover:bg-paper"
                >
                  <td className="font-mono px-5 py-4 text-xs text-obsidian/60">{fmt(q.created_at)}</td>
                  <td className="px-5 py-4">
                    <p className="font-bold text-obsidian">{q.name}</p>
                    {q.company && <p className="text-xs text-obsidian/50">{q.company}</p>}
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-xs text-obsidian/80">{q.email}</p>
                    {q.phone && <p className="text-xs text-obsidian/50">{q.phone}</p>}
                  </td>
                  <td className="px-5 py-4 text-xs font-semibold text-obsidian/80">{q.service_type}</td>
                  <td className="px-5 py-4 text-xs text-obsidian/80">{q.category}</td>
                  <td className="font-mono px-5 py-4 text-xs font-bold text-brand">{q.volume} adet</td>
                  <td className="max-w-[240px] px-5 py-4 text-xs text-obsidian/60">
                    <span className="line-clamp-2">{q.message || "—"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
